import { FormikProps } from 'formik';
import uniqId from 'uniqid';

import { matchData } from '../constant/play';
import { TournamentMatchStatusEnum } from '../types/enums';
import { TournamentCategory } from '../types/tournament';
import { ClassValues } from '../pages/tourDetails/TournamentClasses/components/ClassesTag/ClassesTag';
import { IMatch, PlayValues } from '../pages/tourDetails/TournamentPlay/components/PlayTag/PlayTag';
import {
  MATCHUP_PROGRESSION_ALL_MATCHES,
  MATCHUP_PROGRESSION_BEST_OF_N,
} from './bracketCellMatchupSummary.util';
import {
  countActiveCategoryRubbers,
  resolveTeamMatchupCategorySequence,
  shouldOfferTiebreakerForClass,
} from './teamCategoryRubberCount';

const FINALS = 13;
const SEMI_FINALS = 12;
const QUARTER_FINALS = 11;

export function generateMatchId(sortKey: number, isGold: boolean, round: number): string {
  let matchId = `M${sortKey}-E-R${round}`;
  if (round === FINALS) {
    matchId = isGold ? `M${sortKey}-E-FG` : `M${sortKey}-E-FB`;
  } else if (round === SEMI_FINALS) {
    matchId = `M${sortKey}-E-SF`;
  } else if (round === QUARTER_FINALS) {
    matchId = `M${sortKey}-E-QF`;
  }
  return matchId;
}

export function stripBracketCellMatchIdSuffix(matchId: string): string {
  return matchId
    .replace(/-M\d+$/, '')
    .replace(/-db\d+$/, '')
    .replace(/-tb\d+$/, '');
}

export function buildBracketCellChildMatchId(
  base: string,
  index: number,
  opts?: { isTiebreaker?: boolean }
): string {
  if (opts?.isTiebreaker) {
    return index === 1 ? base : `${base}-tb${index}`;
  }
  return index === 1 ? base : `${base}-M${index}`;
}

function nextSortKey(matches: Record<string, IMatch>): number {
  let max = 0;
  for (const m of Object.values(matches)) {
    if (typeof m.sortKey === 'number' && m.sortKey > max) {
      max = m.sortKey;
    }
  }
  return max + 1;
}

function newBracketCellId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  const hex = uniqId().replace(/-/g, '').padEnd(32, '0').slice(0, 32);
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-4${hex.slice(13, 16)}-8${hex.slice(
    17,
    20
  )}-${hex.slice(20, 32)}`;
}

export type BracketCellCreateContext = {
  variant: 'group' | 'elimination';
  tournamentGroupId?: number;
  round?: number;
  bracket?: number;
  isGold?: boolean;
  sortKey?: number | null;
  /** Section win mode. Omitted for empty, mixed, and manual sections. */
  progressionMode?: number;
};

function activeCategories(categories: TournamentCategory[]): TournamentCategory[] {
  return categories.filter((c) => c.isActive !== false && typeof c.id === 'number');
}

function tiebreakerEnabledForVariant(
  currentClass: ClassValues | undefined,
  variant: 'group' | 'elimination'
): boolean {
  if (variant === 'group') {
    return !!currentClass?.groupTiebreakerEnabled;
  }
  return !!currentClass?.tiebreakerEnabled;
}

export function bracketCellRubberCount(
  context: BracketCellCreateContext,
  currentClass: ClassValues | undefined,
  tournamentCategories: TournamentCategory[]
): number {
  const categories = activeCategories(tournamentCategories);
  const includeTiebreaker =
    shouldOfferTiebreakerForClass(tournamentCategories) &&
    tiebreakerEnabledForVariant(currentClass, context.variant);
  return countActiveCategoryRubbers(categories) + (includeTiebreaker ? 1 : 0);
}

export function recordBracketCellCreate(
  formikArg: FormikProps<PlayValues>,
  context: BracketCellCreateContext,
  currentClass: ClassValues | undefined,
  tournamentCategories: TournamentCategory[],
  tiebreakerLabel: string
): string {
  const cellId = newBracketCellId();
  const isGroup = context.variant === 'group';
  const categories = activeCategories(tournamentCategories);
  const includeTiebreaker =
    shouldOfferTiebreakerForClass(tournamentCategories) &&
    tiebreakerEnabledForVariant(currentClass, context.variant);
  const totalCategoryRubbers = countActiveCategoryRubbers(categories);
  const childCount = totalCategoryRubbers + (includeTiebreaker ? 1 : 0);
  if (childCount === 0) {
    throw new Error('No active categories or decider match to create a bracket cell');
  }
  const round = context.round ?? 1;
  const bracket = context.bracket ?? 0;
  const isGold = context.isGold ?? false;
  const sortKey = context.sortKey ?? nextSortKey(formikArg.values.tournamentMatches);
  const legacyBase = generateMatchId(sortKey, isGold, round);
  const progressionMode =
    context.progressionMode === MATCHUP_PROGRESSION_ALL_MATCHES ||
    context.progressionMode === MATCHUP_PROGRESSION_BEST_OF_N
      ? context.progressionMode
      : isGroup
      ? MATCHUP_PROGRESSION_ALL_MATCHES
      : MATCHUP_PROGRESSION_BEST_OF_N;

  const newMatches: Record<string, IMatch> = { ...formikArg.values.tournamentMatches };
  let index = 0;

  const sequence = resolveTeamMatchupCategorySequence(
    categories,
    currentClass?.teamMatchupSlotOrder
  );
  for (const category of sequence) {
    const categoryId = category.id as number;
    index += 1;
    const id = `99999999-${uniqId()}`;
    newMatches[id] = {
      ...matchData,
      id,
      name: category.name,
      startDate: '',
      homeTeam: null,
      awayTeam: null,
      homePlayer1: null,
      homePlayer2: null,
      awayPlayer1: null,
      awayPlayer2: null,
      homeTeamPlaceholder: '',
      awayTeamPlaceholder: '',
      court: null,
      tournamentGroup: context.tournamentGroupId
        ? ({ id: context.tournamentGroupId } as IMatch['tournamentGroup'])
        : undefined,
      tournamentGroupId: context.tournamentGroupId ?? null,
      tournamentCategory: category,
      tournamentCategoryId: categoryId,
      type: round,
      bracket,
      isGold: String(isGold),
      sortKey,
      matchId: buildBracketCellChildMatchId(legacyBase, index),
      status: TournamentMatchStatusEnum.NOT_STARTED,
      bracketCellId: cellId,
      bracketCellMatchIndex: index,
      bracketCellMatchCount: childCount,
      bracketCellProgressionMode: progressionMode,
      bracketCellStatus: 0,
    } as IMatch;
  }

  if (includeTiebreaker) {
    index += 1;
    const id = `99999999-${uniqId()}`;
    newMatches[id] = {
      ...matchData,
      id,
      name: tiebreakerLabel,
      startDate: '',
      homeTeam: null,
      awayTeam: null,
      homePlayer1: null,
      homePlayer2: null,
      awayPlayer1: null,
      awayPlayer2: null,
      homeTeamPlaceholder: '',
      awayTeamPlaceholder: '',
      court: null,
      tournamentGroup: context.tournamentGroupId
        ? ({ id: context.tournamentGroupId } as IMatch['tournamentGroup'])
        : undefined,
      tournamentGroupId: context.tournamentGroupId ?? null,
      tournamentCategoryId: null,
      type: round,
      bracket,
      isGold: String(isGold),
      sortKey,
      matchId: buildBracketCellChildMatchId(legacyBase, index, { isTiebreaker: true }),
      status: TournamentMatchStatusEnum.NOT_STARTED,
      bracketCellId: cellId,
      bracketCellMatchIndex: index,
      bracketCellMatchCount: childCount,
      bracketCellProgressionMode: progressionMode,
      bracketCellStatus: 0,
      isTiebreaker: true,
      tiebreakerPending: true,
    } as IMatch;
  }

  formikArg.setFieldValue(`bracketCells.${cellId}`, {
    progressionMode,
    matchCount: childCount,
    status: 0,
    round,
    isGold: String(isGold),
    bracket,
    pendingDeletedMatchIds: [],
    isNewCell: true,
  });
  formikArg.setFieldValue('tournamentMatches', newMatches);
  return cellId;
}
